import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { useDebounce } from '@/hooks/useDebounce'
import { FiSearch, FiChevronLeft, FiChevronRight, FiChevronDown } from 'react-icons/fi'
import MetricCard from '@/components/ui/MetricCard'
import InvoiceDetailsModal from './InvoiceDetailsModal'
import { FINANCE_TABS } from '@/constants/financeTabs'
import {
  getInvoices,
  getFinanceOverview,
  getInvoiceCustomers,
  invoiceToTableRow,
  type FinanceMetricTrends,
} from '@/services/api/finance'

type InvoiceRow = ReturnType<typeof invoiceToTableRow>

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    currencyDisplay: 'code',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

import PageSizeSelect from '@/components/common/PageSizeSelect'
import {
  prepareSparkline,
  sparklineColor,
  formatMonthOnMonthTrend,
  buildMetricTrends,
} from '@/utils/metricSparkline'

const FinanceInvoices = () => {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [statementType, setStatementType] = useState<'Activity' | 'Outstanding'>('Outstanding')
  const today = () => new Date().toISOString().slice(0, 10)
  const [startDate, setStartDate] = useState(() => {
    const d = new Date()
    d.setDate(1)
    return d.toISOString().slice(0, 10)
  })
  const [endDate, setEndDate] = useState(today)
  const [asAtDate, setAsAtDate] = useState(today)
  const [customerFilter, setCustomerFilter] = useState('All Customers')
  const [customers, setCustomers] = useState<string[]>([])
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false)
  const [customerSearch, setCustomerSearch] = useState('')
  const customerDropdownRef = useRef<HTMLDivElement | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [resultsPerPage, setResultsPerPage] = useState(10)
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') ?? '')
  const debouncedSearch = useDebounce(searchQuery, 400)
  const [isInvoiceDetailsModalOpen, setIsInvoiceDetailsModalOpen] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceRow | null>(null)
  const [invoicesData, setInvoicesData] = useState<InvoiceRow[]>([])
  const [invoicesLoading, setInvoicesLoading] = useState(true)
  const [invoicesTotal, setInvoicesTotal] = useState(0)
  const [totalBalanceDue, setTotalBalanceDue] = useState(0)
  const [invoicesError, setInvoicesError] = useState<string | null>(null)
  const [metricsLoading, setMetricsLoading] = useState(true)
  const [trendsLoading, setTrendsLoading] = useState(true)
  const [metricsError, setMetricsError] = useState<string | null>(null)
  const [yearSeries, setYearSeries] = useState<{
    profitLoss: { month: string; loss: number; profit: number }[]
    metricTrends?: FinanceMetricTrends
  }>({ profitLoss: [] })
  const [metrics, setMetrics] = useState<{
    totalRevenue: number
    pendingPayments: number
    paidInvoices: number
    overdueAmount: number
    metricTrends?: {
      totalRevenue: number[]
      pendingPayments: number[]
      paidInvoices: number[]
      overdueAmount: number[]
    }
  } | null>(null)

  const zeroMetrics = {
    totalRevenue: 0,
    pendingPayments: 0,
    paidInvoices: 0,
    overdueAmount: 0,
    metricTrends: undefined as
      | {
          totalRevenue: number[]
          pendingPayments: number[]
          paidInvoices: number[]
          overdueAmount: number[]
        }
      | undefined,
  }

  useEffect(() => {
    setMetricsLoading(true)
    setMetricsError(null)
    getFinanceOverview()
      .then((overview) => {
        setMetrics({
          totalRevenue: overview.totalRevenue ?? 0,
          pendingPayments: overview.pendingPayments ?? 0,
          paidInvoices: overview.paidInvoices ?? 0,
          overdueAmount: overview.overdueAmount ?? 0,
          metricTrends: overview.metricTrends,
        })
      })
      .catch(() => {
        setMetrics(zeroMetrics)
        setMetricsError('Couldn\'t load latest data.')
      })
      .finally(() => setMetricsLoading(false))
  }, [])

  useEffect(() => {
    let cancelled = false
    setTrendsLoading(true)
    getFinanceOverview({ timeframe: 'year' })
      .then((data) => {
        if (!cancelled) {
          setYearSeries({
            profitLoss: data.profitLoss ?? [],
            metricTrends: data.metricTrends,
          })
        }
      })
      .catch(() => {
        if (!cancelled) setYearSeries({ profitLoss: [] })
      })
      .finally(() => {
        if (!cancelled) setTrendsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    getInvoiceCustomers()
      .then(({ customers: list }) => setCustomers(list))
      .catch(() => setCustomers([]))
  }, [])

  useEffect(() => {
    if (!customerDropdownOpen) return
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(target)) {
        setCustomerDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [customerDropdownOpen])

  const fetchInvoices = useCallback(() => {
    setInvoicesLoading(true)
    setInvoicesError(null)
    getInvoices({
      page: currentPage,
      limit: resultsPerPage,
      search: debouncedSearch || undefined,
      statementType: statementType === 'Outstanding' ? 'outstanding' : undefined,
      customer: customerFilter !== 'All Customers' ? customerFilter : undefined,
      startDate: statementType === 'Activity' ? startDate || undefined : undefined,
      endDate: statementType === 'Activity' ? endDate || undefined : undefined,
      asAtDate: statementType === 'Outstanding' ? asAtDate || undefined : undefined,
    })
      .then(({ invoices, meta, totalBalanceDue: balance }) => {
        setInvoicesData(invoices.map(invoiceToTableRow))
        setInvoicesTotal(meta?.total ?? invoices.length)
        setTotalBalanceDue(balance ?? 0)
      })
      .catch((err: any) => {
        setInvoicesData([])
        setInvoicesTotal(0)
        setTotalBalanceDue(0)
        setInvoicesError(err?.response?.data?.error?.message || err?.message || 'Failed to load invoices.')
      })
      .finally(() => setInvoicesLoading(false))
  }, [currentPage, debouncedSearch, statementType, customerFilter, startDate, endDate, asAtDate, resultsPerPage])

  useEffect(() => {
    fetchInvoices()
  }, [fetchInvoices])

  useEffect(() => {
    const q = searchParams.get('search')
    if (q !== null) setSearchQuery(q)
  }, [searchParams])

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchInvoices()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [fetchInvoices])

  const tabs = FINANCE_TABS

  const safeMetrics = metrics ?? zeroMetrics
  const metricTrends = buildMetricTrends(
    yearSeries.metricTrends ?? safeMetrics.metricTrends,
    yearSeries.profitLoss,
    {
      totalRevenue: safeMetrics.totalRevenue,
      paidInvoices: safeMetrics.paidInvoices,
      pendingPayments: safeMetrics.pendingPayments,
      overdueAmount: safeMetrics.overdueAmount,
    }
  )
  const metricsSparklinesReady = !trendsLoading
  const totalResults = invoicesTotal
  const totalPages = Math.max(1, Math.ceil(totalResults / resultsPerPage))
  const showingCount = totalResults === 0 ? 0 : Math.min(resultsPerPage, invoicesData.length)
  const filteredCustomers = customers.filter((customer) =>
    customer.toLowerCase().includes(customerSearch.trim().toLowerCase())
  )

  return (
    <div className="space-y-6" style={{ maxWidth: '100%' }}>
      {/* Header Section */}
      <div>
        {/* First Row: Invoice title and Breadcrumbs */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
          <h1
            className="text-lg sm:text-xl"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: 'clamp(18px, 4vw, 20px)',
              lineHeight: '28px',
              letterSpacing: '0%',
              color: '#212126',
            }}
          >
            Invoices
          </h1>
          
          {/* Breadcrumbs on the right of Finance title */}
          <span
            className="text-xs sm:text-sm"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: 'clamp(11px, 2.5vw, 12px)',
              lineHeight: '28px',
              letterSpacing: '0%',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span style={{ color: '#69697A' }}>Home</span>
            <span style={{ color: '#69697A', margin: '0 4px' }}> &gt; </span>
            <span style={{ color: '#212126' }}>Invoices</span>
          </span>
        </div>

        {/* Second Row: Tabs and Controls on the same level */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 width-full">
          {/* Tabs - full width so all tabs (including Business Analytics) are visible */}
          <div 
            className="flex items-center overflow-x-auto scrollbar-hide flex-1 min-w-0"
            style={{
              width: '100%',
              minWidth: 0,
              height: '38px',
              borderRadius: '8px',
              border: '1px solid #E9E9EA',
              backgroundColor: '#FFFFFF',
              padding: '4px',
              gap: '0px'
            }}
          >
            {tabs.map((tab) => {
              const isActive = location.pathname === tab.path
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    color: isActive ? '#FFFFFF' : '#69697A',
                    backgroundColor: isActive ? '#212126' : 'transparent',
                    padding: '6px 8px',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    justifyContent: 'center',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {tab.label}
                </Link>
              )
            })}
          </div>

          {/* Export */}
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0" style={{ height: '40px' }}>
            <button
              className="flex items-center justify-center gap-2 sm:gap-3 font-medium rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
              style={{
                minWidth: '100px',
                width: 'auto',
                height: '38px',
                color: '#212126',
                border: '1px solid #212126',
                padding: '8px 16px',
                borderRadius: '6px',
              }}
            >
              <img 
                src="/images/download.svg" 
                alt="Download" 
                className="w-4 h-4" 
                style={{ 
                  filter: 'brightness(0) saturate(100%) invert(8%) sepia(4%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(95%)'
                }} 
              />
              <span className="text-sm sm:text-base">Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards - synced with invoice data (Overview API) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <MetricCard
          title="Total Invoices"
          value={metricsLoading ? '…' : formatCurrency(safeMetrics.totalRevenue)}
          trend={!metricsSparklinesReady ? '' : formatMonthOnMonthTrend(metricTrends.totalRevenue)}
          graphData={!metricsSparklinesReady ? undefined : prepareSparkline(metricTrends.totalRevenue)}
          graphColor={sparklineColor(metricTrends.totalRevenue)}
          icon={
            <img 
              src="/images/dollar-circle.svg" 
              alt="Dollar" 
              className="w-5 h-5" 
              style={{
                filter: 'brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(103deg) brightness(95%) contrast(85%)'
              }}
            />
          }
          iconBgColor=""
          iconBgColorCustom="#E8F7EF"
          style={{ width: '100%' }}
        />
        <MetricCard
          title="Pending Invoices"
          value={metricsLoading ? '…' : formatCurrency(safeMetrics.pendingPayments)}
          trend={!metricsSparklinesReady ? '' : formatMonthOnMonthTrend(metricTrends.pendingPayments)}
          graphData={!metricsSparklinesReady ? undefined : prepareSparkline(metricTrends.pendingPayments)}
          graphColor={sparklineColor(metricTrends.pendingPayments, true)}
          icon={<img src="/images/empty-wallet-time.svg" alt="Pending Invoices" className="w-5 h-5" />}
          iconBgColor=""
          iconBgColorCustom="#FEF9EB"
          style={{ width: '100%' }}
        />
        <MetricCard
          title="Paid Invoices"
          value={metricsLoading ? '…' : formatCurrency(safeMetrics.paidInvoices)}
          trend={!metricsSparklinesReady ? '' : formatMonthOnMonthTrend(metricTrends.paidInvoices)}
          graphData={!metricsSparklinesReady ? undefined : prepareSparkline(metricTrends.paidInvoices)}
          graphColor={sparklineColor(metricTrends.paidInvoices)}
          icon={<img src="/images/wallet-check.svg" alt="Paid Invoices" className="w-5 h-5" />}
          iconBgColor=""
          iconBgColorCustom="#EAF1FF"
          style={{ width: '100%' }}
        />
        <MetricCard
          title="Overdue Invoices"
          value={metricsLoading ? '…' : formatCurrency(safeMetrics.overdueAmount)}
          trend={!metricsSparklinesReady ? '' : formatMonthOnMonthTrend(metricTrends.overdueAmount)}
          graphData={!metricsSparklinesReady ? undefined : prepareSparkline(metricTrends.overdueAmount)}
          graphColor={sparklineColor(metricTrends.overdueAmount, true)}
          icon={<img src="/images/clock.svg" alt="Overdue Invoices" className="w-5 h-5" />}
          iconBgColor=""
          iconBgColorCustom="#F2F4F2"
          style={{ width: '100%' }}
        />
      </div>
      {metricsError && (
        <p className="text-sm text-amber-600" style={{ fontFamily: 'Lexend Deca' }}>
          {metricsError} Showing ZAR 0 for all metrics.
        </p>
      )}

      {/* Invoices Table Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 sm:mb-6 gap-4">
          <h3
            className="text-sm sm:text-base"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: 'clamp(13px, 3vw, 14px)',
              lineHeight: '28px',
              letterSpacing: '0%',
              color: '#212126',
            }}
          >
            Invoices
          </h3>

          {/* Filters: Statement Type, Date Range, Customer, Search, Update */}
          <div
            className="flex flex-wrap items-center gap-2 sm:gap-4 w-full"
            style={{ padding: '12px 16px', backgroundColor: '#EFF6FF', borderRadius: '8px', border: '1px solid #BFDBFE' }}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '13px', color: '#212126' }}>Statement Type</span>
              <select
                value={statementType}
                onChange={(e) => setStatementType(e.target.value as 'Activity' | 'Outstanding')}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ fontFamily: 'Lexend Deca', minWidth: '120px' }}
              >
                <option value="Activity">Activity</option>
                <option value="Outstanding">Outstanding</option>
              </select>
              {statementType === 'Activity' ? (
                <>
                  <span style={{ fontFamily: 'Lexend Deca', fontSize: '13px', color: '#69697A' }}>between</span>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ fontFamily: 'Lexend Deca' }}
                  />
                  <span style={{ fontFamily: 'Lexend Deca', fontSize: '13px', color: '#69697A' }}>and</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ fontFamily: 'Lexend Deca' }}
                  />
                </>
              ) : (
                <>
                  <span style={{ fontFamily: 'Lexend Deca', fontSize: '13px', color: '#69697A' }}>as at</span>
                  <input
                    type="date"
                    value={asAtDate}
                    onChange={(e) => setAsAtDate(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ fontFamily: 'Lexend Deca' }}
                  />
                </>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div
                ref={customerDropdownRef}
                className="relative"
                style={{ minWidth: '180px', maxWidth: '260px' }}
              >
                <button
                  type="button"
                  onClick={() => {
                    const nextOpen = !customerDropdownOpen
                    setCustomerDropdownOpen(nextOpen)
                    if (!nextOpen) setCustomerSearch('')
                  }}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-left bg-white"
                  style={{ fontFamily: 'Lexend Deca' }}
                  title={customerFilter}
                >
                  <span className="block truncate">{customerFilter}</span>
                </button>
                <FiChevronDown
                  className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ width: '16px', height: '16px', color: '#69697A' }}
                />
                {customerDropdownOpen && (
                  <div
                    className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-20"
                    style={{ fontFamily: 'Lexend Deca' }}
                  >
                    <div className="p-2 border-b border-gray-100">
                      <div className="relative">
                        <FiSearch
                          className="absolute left-2.5 top-1/2 -translate-y-1/2"
                          style={{ width: '14px', height: '14px', color: '#69697A' }}
                        />
                        <input
                          type="text"
                          value={customerSearch}
                          onChange={(e) => setCustomerSearch(e.target.value)}
                          placeholder="Search customer"
                          className="w-full pl-8 pr-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          style={{ fontFamily: 'Lexend Deca' }}
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="max-h-52 overflow-y-auto py-1">
                      <button
                        type="button"
                        onClick={() => {
                          setCustomerFilter('All Customers')
                          setCustomerDropdownOpen(false)
                          setCustomerSearch('')
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                        style={{ color: customerFilter === 'All Customers' ? '#2563EB' : '#212126' }}
                      >
                        All Customers
                      </button>
                      {filteredCustomers.length > 0 ? (
                        filteredCustomers.map((customer) => (
                          <button
                            key={customer}
                            type="button"
                            onClick={() => {
                              setCustomerFilter(customer)
                              setCustomerDropdownOpen(false)
                              setCustomerSearch('')
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                            style={{ color: customerFilter === customer ? '#2563EB' : '#212126' }}
                            title={customer}
                          >
                            <span className="block truncate">{customer}</span>
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500">No customers found.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="relative" style={{ minWidth: '140px', maxWidth: '200px' }}>
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2" style={{ width: '16px', height: '16px', color: '#69697A' }} />
                <input
                  type="text"
                  placeholder="Search Invoices"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ fontFamily: 'Lexend Deca' }}
                />
              </div>
              <button
                onClick={() => { setCurrentPage(1); fetchInvoices(); }}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition-opacity"
                style={{ fontFamily: 'Lexend Deca', backgroundColor: '#2563EB' }}
              >
                Update
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="w-full overflow-x-auto sm:overflow-visible" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #E9E9EA', width: '100%', minWidth: '800px' }}>
            <table className="w-full" style={{ borderCollapse: 'collapse', minWidth: '800px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #E9E9EA' }}>
                <th className="text-left py-2" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#69697A', paddingLeft: '16px', borderTopLeftRadius: '8px' }}>Date</th>
                <th className="text-left py-2" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#69697A', paddingLeft: '16px' }}>Activity</th>
                <th className="text-left py-2" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#69697A', paddingLeft: '16px' }}>Reference</th>
                <th className="text-left py-2" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#69697A', paddingLeft: '16px' }}>Due Date</th>
                <th className="text-right py-2" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#69697A', paddingRight: '16px' }}>Invoice Amount</th>
                <th className="text-right py-2" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#69697A', paddingRight: '16px' }}>Payments</th>
                <th className="text-right py-2" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#69697A', paddingRight: '16px', borderTopRightRadius: '8px' }}>Balance ZAR</th>
              </tr>
            </thead>
            <tbody>
              {invoicesLoading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center" style={{ fontFamily: 'Lexend Deca', fontSize: '14px', color: '#69697A', borderBottom: '1px solid #E9E9EA' }}>
                    Loading…
                  </td>
                </tr>
              ) : invoicesData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center" style={{ fontFamily: 'Lexend Deca', fontSize: '14px', color: invoicesError ? '#DC2626' : '#69697A', borderBottom: '1px solid #E9E9EA' }}>
                    {invoicesError || 'No invoices yet. Sync from Xero (Settings → Integrations) or add an invoice.'}
                  </td>
                </tr>
              ) : (
                invoicesData.map((invoice, index) => {
                const isLastRow = index === invoicesData.length - 1
                return (
                  <tr key={invoice.id} style={{ borderBottom: isLastRow ? 'none' : '1px solid #E9E9EA' }}>
                    <td className="py-3" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '16px', ...(isLastRow && { borderBottomLeftRadius: '8px' }) }}>
                      {invoice.date}
                    </td>
                    <td className="py-3" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', paddingLeft: '16px' }}>
                      <button
                        type="button"
                        onClick={() => { setSelectedInvoice(invoice); setIsInvoiceDetailsModalOpen(true) }}
                        style={{
                          fontFamily: 'Lexend Deca',
                          color: '#2C71F6',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                        }}
                      >
                        Invoice #{invoice.number}
                      </button>
                    </td>
                    <td className="py-3" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '16px' }}>
                      {invoice.ref}
                    </td>
                    <td className="py-3" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingLeft: '16px' }}>
                      {invoice.dueDate}
                    </td>
                    <td className="py-3 text-right" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingRight: '16px' }}>
                      {invoice.invoiceAmount}
                    </td>
                    <td className="py-3 text-right" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingRight: '16px' }}>
                      {invoice.payments}
                    </td>
                    <td className="py-3 text-right" style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '13px', lineHeight: '22px', color: '#212126', paddingRight: '16px', ...(isLastRow && { borderBottomRightRadius: '8px' }) }}>
                      {invoice.balanceZar}
                    </td>
                  </tr>
                )
              })
              )}
            </tbody>
            <tfoot>
              <tr>
                <td
                  colSpan={5}
                  style={{
                    padding: '16px 16px 12px',
                    borderTop: '1px solid #E9E9EA',
                    borderBottom: '2px solid #212126',
                    fontFamily: 'Lexend Deca',
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#212126',
                  }}
                >
                  BALANCE DUE ZAR
                </td>
                <td
                  colSpan={2}
                  className="text-right"
                  style={{
                    padding: '16px 16px 12px',
                    borderTop: '1px solid #E9E9EA',
                    borderBottom: '2px solid #212126',
                    fontFamily: 'Lexend Deca',
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#212126',
                  }}
                >
                  {totalBalanceDue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
              </tr>
              <tr>
                <td 
                  colSpan={7}
                  style={{
                    padding: '16px',
                    borderTop: '1px solid #E9E9EA',
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontSize: '14px',
                        color: '#69697A',
                      }}
                    >
                      Showing {showingCount} of {totalResults} Results
                    </span>
                    <div className="flex items-center gap-2">
                      <PageSizeSelect
                        value={resultsPerPage}
                        onChange={(size) => {
                          setResultsPerPage(size)
                          setCurrentPage(1)
                        }}
                      />
                      <button
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="flex items-center gap-2 px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontSize: '14px',
                          color: '#212126',
                        }}
                      >
                        <FiChevronLeft className="w-4 h-4" />
                        <span>Previous</span>
                      </button>
                      {[1, 2, 3].map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-1 border rounded ${
                            currentPage === page
                              ? 'bg-gray-900 text-white border-gray-900'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontSize: '14px',
                            color: currentPage === page ? '#FFFFFF' : '#212126',
                          }}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="flex items-center gap-2 px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontSize: '14px',
                          color: '#212126',
                        }}
                      >
                        <span>Next</span>
                        <FiChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
          </div>
        </div>
      </div>

      {/* Invoice Details Modal */}
      <InvoiceDetailsModal
        isOpen={isInvoiceDetailsModalOpen}
        onClose={() => {
          setIsInvoiceDetailsModalOpen(false)
          setSelectedInvoice(null)
        }}
        invoiceData={selectedInvoice || undefined}
      />
    </div>
  )
}

export default FinanceInvoices

